import type { ProductCategoryId } from './constants'

/**
 * 설명 화면(A2) 맨 위의 맛/스타일 칩. 카테고리별 고정 6~8개, 복수 선택, 건너뛰기 가능.
 * 선택값은 생성의 방향 시드에 들어간다 (id 를 서버로 보냄, 라벨은 기기 언어로 표시).
 */
export interface TasteChip {
  id: string
  en: string
  ko: string
  ja: string
  zh: string
}

const c = (id: string, en: string, ko: string, ja: string, zh: string): TasteChip => ({
  id,
  en,
  ko,
  ja,
  zh,
})

/** 기기 언어의 라벨 (없으면 영어) */
export function tasteChipLabel(chip: TasteChip, language: string): string {
  if (language === 'ko') return chip.ko
  if (language === 'ja') return chip.ja
  if (language === 'zh') return chip.zh
  return chip.en
}

const COMMON = {
  spicy: c('spicy', 'Spicy', '매콤', '辛い', '辣'),
  sweet: c('sweet', 'Sweet', '달콤', '甘い', '甜'),
  savory: c('savory', 'Savory', '감칠맛', '旨味', '鲜'),
  nutty: c('nutty', 'Nutty', '고소', '香ばしい', '香'),
  tangy: c('tangy', 'Tangy', '새콤', '酸味', '酸'),
  smoky: c('smoky', 'Smoky', '스모키', 'スモーキー', '烟熏'),
  mild: c('mild', 'Mild & clean', '담백', 'あっさり', '清淡'),
  lowSugar: c('low-sugar', 'Low sugar', '저당', '低糖', '低糖'),
  lowSodium: c('low-sodium', 'Low sodium', '저염', '減塩', '低盐'),
  premium: c('premium', 'Premium', '프리미엄', 'プレミアム', '高端'),
  kids: c('kids', 'Kid-friendly', '아이용', '子ども向け', '儿童友好'),
  herbal: c('herbal', 'Herbal', '허브', 'ハーブ', '香草'),
  creamy: c('creamy', 'Creamy', '크리미', 'クリーミー', '奶香'),
  crunchy: c('crunchy', 'Crunchy', '바삭', 'サクサク', '酥脆'),
  chewy: c('chewy', 'Chewy', '쫄깃', 'もちもち', '有嚼劲'),
  rich: c('rich', 'Rich', '진한', '濃厚', '浓郁'),
  light: c('light', 'Light', '가벼운', '軽い', '清爽'),
  fruity: c('fruity', 'Fruity', '과일향', 'フルーティー', '果香'),
  floral: c('floral', 'Floral', '꽃향', '花の香り', '花香'),
  roasted: c('roasted', 'Roasted', '로스팅', 'ロースト', '烘焙'),
  fermented: c('fermented', 'Funky & fermented', '발효향', '発酵の風味', '发酵风味'),
  vegan: c('vegan', 'Vegan', '비건', 'ヴィーガン', '纯素'),
  highProtein: c('high-protein', 'High protein', '고단백', '高たんぱく', '高蛋白'),
  glutenFree: c('gluten-free', 'Gluten-free', '글루텐프리', 'グルテンフリー', '无麸质'),
  sparkling: c('sparkling', 'Sparkling', '탄산', '炭酸', '气泡'),
  unsweetened: c('unsweetened', 'Unsweetened', '무가당', '無糖', '无糖'),
  dry: c('dry', 'Dry', '드라이', 'ドライ', '干型'),
  aged: c('aged', 'Aged', '숙성', '熟成', '陈酿'),
  buttery: c('buttery', 'Buttery', '버터향', 'バター風味', '黄油香'),
  wholeGrain: c('whole-grain', 'Whole grain', '통곡물', '全粒', '全谷物'),
  cold: c('cold', 'Cold-brew / chilled', '콜드', 'コールド', '冷萃/冰镇'),
}

export const TASTE_CHIPS: Record<ProductCategoryId, TasteChip[]> = {
  meal: [
    COMMON.spicy,
    COMMON.savory,
    COMMON.mild,
    COMMON.highProtein,
    COMMON.lowSodium,
    COMMON.kids,
    COMMON.vegan,
    COMMON.premium,
  ],
  sauce: [
    COMMON.spicy,
    COMMON.sweet,
    COMMON.tangy,
    COMMON.smoky,
    COMMON.herbal,
    COMMON.savory,
    COMMON.lowSugar,
    COMMON.vegan,
  ],
  bakery: [
    COMMON.buttery,
    COMMON.wholeGrain,
    COMMON.sweet,
    COMMON.savory,
    COMMON.chewy,
    COMMON.crunchy,
    COMMON.lowSugar,
    COMMON.vegan,
  ],
  snacks: [
    COMMON.crunchy,
    COMMON.spicy,
    COMMON.sweet,
    COMMON.savory,
    COMMON.nutty,
    COMMON.lowSodium,
    COMMON.highProtein,
    COMMON.kids,
  ],
  dessert: [
    COMMON.sweet,
    COMMON.creamy,
    COMMON.rich,
    COMMON.nutty,
    COMMON.fruity,
    COMMON.lowSugar,
    COMMON.vegan,
    COMMON.premium,
  ],
  beverage: [
    COMMON.sparkling,
    COMMON.unsweetened,
    COMMON.sweet,
    COMMON.fruity,
    COMMON.floral,
    COMMON.herbal,
    COMMON.cold,
    COMMON.creamy,
  ],
  juice: [
    COMMON.unsweetened,
    COMMON.sweet,
    COMMON.tangy,
    COMMON.cold,
    COMMON.light,
    COMMON.fruity,
    COMMON.premium,
  ],
  alcohol: [
    COMMON.dry,
    COMMON.sweet,
    COMMON.fruity,
    COMMON.sparkling,
    COMMON.aged,
    COMMON.light,
    COMMON.rich,
    COMMON.premium,
  ],
  dairy: [
    COMMON.creamy,
    COMMON.rich,
    COMMON.tangy,
    COMMON.mild,
    COMMON.highProtein,
    COMMON.lowSugar,
    COMMON.aged,
    COMMON.kids,
  ],
  noodles: [
    COMMON.chewy,
    COMMON.spicy,
    COMMON.savory,
    COMMON.mild,
    COMMON.wholeGrain,
    COMMON.glutenFree,
    COMMON.highProtein,
    COMMON.cold,
  ],
  'soup-base': [
    COMMON.spicy,
    COMMON.savory,
    COMMON.rich,
    COMMON.mild,
    COMMON.smoky,
    COMMON.lowSodium,
    COMMON.vegan,
  ],
  'processed-meat': [
    COMMON.smoky,
    COMMON.spicy,
    COMMON.savory,
    COMMON.sweet,
    COMMON.lowSodium,
    COMMON.highProtein,
    COMMON.premium,
  ],
  'processed-seafood': [
    COMMON.spicy,
    COMMON.savory,
    COMMON.chewy,
    COMMON.mild,
    COMMON.smoky,
    COMMON.lowSodium,
    COMMON.premium,
  ],
  'grain-legume-nut': [
    COMMON.nutty,
    COMMON.roasted,
    COMMON.crunchy,
    COMMON.unsweetened,
    COMMON.sweet,
    COMMON.highProtein,
    COMMON.wholeGrain,
    COMMON.vegan,
  ],
  'fruit-vegetable': [
    COMMON.sweet,
    COMMON.tangy,
    COMMON.lowSugar,
    COMMON.chewy,
    COMMON.crunchy,
    COMMON.fruity,
    COMMON.premium,
  ],
  fermented: [
    COMMON.spicy,
    COMMON.tangy,
    COMMON.fermented,
    COMMON.mild,
    COMMON.lowSodium,
    COMMON.aged,
    COMMON.vegan,
  ],
  'fats-oils': [
    COMMON.spicy,
    COMMON.nutty,
    COMMON.roasted,
    COMMON.herbal,
    COMMON.smoky,
    COMMON.premium,
    COMMON.light,
  ],
  other: [
    COMMON.spicy,
    COMMON.sweet,
    COMMON.savory,
    COMMON.nutty,
    COMMON.tangy,
    COMMON.mild,
    COMMON.premium,
    COMMON.vegan,
  ],
}

/** id 목록 → 프롬프트용 영어 라벨 */
export function tasteLabels(categoryId: ProductCategoryId, ids: string[]): string[] {
  const list = TASTE_CHIPS[categoryId]
  return ids.map((id) => list.find((t) => t.id === id)?.en ?? id)
}

/**
 * 클릭식 맛 고르기(taste-axes.ts)의 맛 축과 뜻이 겹쳐 축으로 옮겨 간 칩 (2026-10-30 대표 결정 1).
 * 맛 축 카드를 그릴 때는 나머지 칩만 '스타일' 줄로 남긴다. 옛 결과의 input.tastes 는 그대로 읽힌다
 */
const CHIPS_MOVED_TO_AXES: readonly string[] = [
  'spicy',
  'sweet',
  'tangy',
  'savory',
  'smoky',
  'crunchy',
  'chewy',
  'creamy',
  'rich',
  'mild',
  'sparkling',
  'unsweetened',
  'nutty',
  'roasted',
  'aged',
  'dry',
]

/** 맛 축 카드 아래 '스타일' 칩 (축과 겹치지 않는 칩만) */
export function styleTasteChips(categoryId: ProductCategoryId): TasteChip[] {
  // 개발용 바로가기처럼 표에 없는 카테고리가 들어와도 화면이 죽지 않게 빈 줄
  return (TASTE_CHIPS[categoryId] ?? []).filter((chip) => !CHIPS_MOVED_TO_AXES.includes(chip.id))
}
